if (!customElements.get('additional-products-carousel')) {
  class AdditionalProductsCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.swiperId = null;
      this.carouselElement = null;
      this.carouselItemSelector = '.swiper-slide';
      this.observer = null;
      this.apiUrl = '';
    }

    connectedCallback() {
      this.swiperId = this.getAttribute('data-swiper-id');
      this.carouselElement = this.querySelector(`#${this.swiperId}`);
      this.apiUrl = this.getAttribute('data-api-url');

      if (this.carouselElement) {
        this.setupIntersectionObserver();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.swiperInstance) {
        this.destroySlider();
      }
    }

    /**
     * Sets up an IntersectionObserver to load product recommendations when the carousel enters the viewport.
     *
     * Uses IntersectionObserver if available, otherwise falls back to immediate data fetch.
     *
     * @returns {void}
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.fetchDataOnView(); // Fallback for browsers without Intersection Observer
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.fetchDataOnView();
              observer.disconnect(); // Stop observing after loading
            }
          });
        },
        { rootMargin: '200px' },
      ); // Start loading when user is ~200px near

      this.observer.observe(this);
    }

    /**
     * Fetches and displays product recommendations when the carousel comes into view.
     *
     * Calls fetchData to retrieve product data and updates the carousel's visibility.
     * Handles errors gracefully and ensures the carousel is visible even on failure.
     *
     * @returns {void}
     */
    fetchDataOnView() {
      const recommendationsPromise = this.fetchData(
        this.apiUrl,
        this.carouselItemSelector,
      );

      Promise.all([recommendationsPromise])
        .then(() => {
          this.style.visibility = 'visible';
        })
        .catch((error) => {
          console.error(
            'Error loading product recommendations or recent products:',
            error,
          );
          this.style.visibility = 'visible'; // Ensure visibility even if fetch fails
        });
    }

    /**
     * Fetches product recommendation data when the carousel comes into view.
     *
     * @param {string} apiUrl - The API endpoint URL to fetch product data from. Shopify product recommendations endpoint is expected.
     * @param {string} selector - CSS selector to extract product elements from the fetched HTML.
     * @returns {Promise<void>} Resolves when data is fetched and appended, or logs error on failure.
     */
    async fetchData(apiUrl, selector) {
      try {
        const response = await fetch(apiUrl);
        if (!response.ok)
          throw new Error(`Error fetching data: ${response.status}`);

        const data = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(data, 'text/html');
        // Extract all elements matching the selector from the fetched HTML document.
        const fetchedProducts = Array.from(doc.querySelectorAll(selector));

        /**
         * Filters the fetched product elements to include only those
         * with the 'data-additional-product-slide' attribute.
         *
         * @type {HTMLElement[]}
         */
        const filteredProducts = fetchedProducts.filter((product) => {
          return product.hasAttribute('data-additional-product-slide');
        });

        if (filteredProducts.length === 0) {
          console.warn(
            `No products found with selector "${selector}" in the fetched data from ${apiUrl}. Check if the API response contains the expected HTML structure and elements.`,
          );

          return;
        }

        this.appendContent(filteredProducts);
      } catch (error) {
        console.error(`Failed to fetch data from ${apiUrl}:`, error);
      }
    }

    /**
     * Appends fetched product elements to the Swiper carousel and initializes the slider.
     *
     * @param {HTMLElement[]} products - Array of product elements to append as slides.
     * @returns {void}
     */
    appendContent(products) {
      const swiperWrapper =
        this.carouselElement.querySelector('.swiper-wrapper');

      products.forEach((product) => {
        swiperWrapper.appendChild(product);
      });

      this.initProductBlockSlider();
    }

    initProductBlockSlider() {
      if (!this.carouselElement) {
        console.warn(
          'Additional Products: Carousel element not found. Cannot initialize Swiper.',
        );

        return null;
      }

      this.swiperInstance = new RevolutionSwiper(this.carouselElement);
    }

    destroySlider() {
      if (this.swiperInstance.swiperElement) {
        try {
          if (this.swiperInstance.swiperElement.destroyed) {
            console.info('Swiper already destroyed');
            this.swiperInstance = null;
            return;
          }
          this.swiperInstance.swiperElement.destroy(true, true);
          this.swiperInstance = null;
        } catch (error) {
          console.error('Error destroying Swiper:', error);
          this.swiperInstance = null;
        }
      } else {
        console.log('No Swiper instance to destroy');
      }
    }
  }

  customElements.define(
    'additional-products-carousel',
    AdditionalProductsCarousel,
  );
}
